// Collapsible left sidebar with folder tree, nav links, and footer.
// Context-aware: swaps header/list based on current route.

import React, { useState, useCallback, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSessionContext } from '../../contexts/SessionContext';
import { useRunningSessionIds } from '../../contexts/ActiveStreamsContext';
import { useGraphStorageContext } from '../../contexts/GraphStorageContext';
import { useTheme } from '../../contexts/ThemeContext';
import FolderTree from './FolderTree';
import SavedGraphList from './SavedGraphList';
import ChatItemMenu from './ChatItemMenu';
import GraphItemMenu from './GraphItemMenu';
import FolderMenu from './FolderMenu';
import ClearAllDialog from './ClearAllDialog';
import {
  chatToMarkdownPlain,
  chatToMarkdownDetailed,
  triggerDownload,
  sanitizeFilename,
  formatDate,
} from '../../lib/export';
import { hydrateSessions } from '../../lib/chatAdapters';
import { chatKey } from '../../lib/queries';
import { getChat, type ChatDetail } from '../../lib/feedbackApi';
import { buildChatDebugBundle } from '../../lib/debugExport';
import type { ChatSession } from '../../types';
import '../../styles/sidebar-chats.css';
import fkieLogo from '../../assets/fraunhofer-fkie-logo.png';
import fkieLogoDark from '../../assets/fraunhofer-fkie-logo-dark.png';

export interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

type ActiveRoute = 'home' | 'chat' | 'graph' | 'overview' | 'search' | 'explore' | 'trends';

function resolveRoute(pathname: string): ActiveRoute {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/chat')) return 'chat';
  if (pathname.startsWith('/overview')) return 'overview';
  if (pathname.startsWith('/graph')) return 'graph';
  if (pathname.startsWith('/search')) return 'search';
  if (pathname.startsWith('/explore')) return 'explore';
  if (pathname.startsWith('/trends')) return 'trends';
  return 'home';
}

export default function Sidebar({ collapsed, onToggleCollapse }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolved } = useTheme();
  const activeRoute = resolveRoute(location.pathname);

  // Chat context
  const ctx = useSessionContext();
  const runningIds = useRunningSessionIds();
  const {
    sessions,
    activeSessionId,
    createSession,
    switchSession,
    deleteSession,
    deleteSessions,
    setSessionPinned,
    renameSession,
    clearAllSessions,
    folders,
    createFolder,
    renameFolder,
    deleteFolder,
    moveSessionToFolder,
    folderCollapseState,
    toggleFolderCollapse,
    openExportDialog,
  } = ctx;

  // Graph context
  const graphCtx = useGraphStorageContext();
  const {
    savedGraphs,
    activeGraphId,
    deleteGraph,
    renameGraph,
    setActiveGraphId,
    clearAllGraphs,
  } = graphCtx;

  // Inline rename state — supports session, folder, and graph
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renamingType, setRenamingType] = useState<'session' | 'folder' | 'graph' | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Context menu state
  const [chatMenuState, setChatMenuState] = useState<{ sessionId: string; rect: DOMRect } | null>(null);
  const [folderMenuState, setFolderMenuState] = useState<{ folderId: string; rect: DOMRect } | null>(null);
  const [graphMenuState, setGraphMenuState] = useState<{ graphId: string; rect: DOMRect } | null>(null);

  // Clear-all confirmation
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  // Chat list filter + multi-select (UI state only, not persisted).
  const [chatFilter, setChatFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const qc = useQueryClient();

  // Drop selected ids whose chats disappeared (deleted elsewhere, etc.).
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const alive = new Set(sessions.map((s) => s.id));
    let changed = false;
    const next = new Set<string>();
    for (const id of selectedIds) {
      if (alive.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) setSelectedIds(next);
  }, [sessions, selectedIds]);

  // Leaving the chat list clears the selection.
  useEffect(() => {
    if (activeRoute !== 'chat') setSelectedIds(new Set());
  }, [activeRoute]);

  // The filter also matches message text we already hold in memory:
  // drafts/temps carry their messages, and any chat opened this session
  // has its turns in the react-query cache. No extra requests.
  const getSearchTexts = useCallback((session: ChatSession): string[] => {
    const texts = session.messages.map((m) => m.content);
    const detail = qc.getQueryData<ChatDetail>(chatKey(session.id));
    if (detail) for (const t of detail.turns) texts.push(t.query, t.answer);
    return texts;
  }, [qc]);

  const moveSessionsToFolder = useCallback((ids: string[], folderId: string | undefined) => {
    for (const id of ids) moveSessionToFolder(id, folderId);
  }, [moveSessionToFolder]);

  const handleBulkMove = useCallback((value: string) => {
    if (!value) return;
    const ids = Array.from(selectedIds);
    const folderId = value === '__none__' ? undefined : value;
    moveSessionsToFolder(ids, folderId);
    const name = folderId ? folders.find((f) => f.id === folderId)?.name ?? 'folder' : 'no folder';
    toast.success(`Moved ${ids.length} chat${ids.length === 1 ? '' : 's'} to ${name}`);
  }, [selectedIds, moveSessionsToFolder, folders]);

  const handleBulkDelete = useCallback(() => {
    const ids = Array.from(selectedIds);
    deleteSessions(ids);
    setSelectedIds(new Set());
    setConfirmBulkDelete(false);
    toast.success(`Deleted ${ids.length} chat${ids.length === 1 ? '' : 's'}`);
  }, [selectedIds, deleteSessions]);

  // ── Rename helpers ───────────────────────────────────────────────────

  const startRenameSession = useCallback((id: string, currentTitle: string) => {
    setRenamingId(id);
    setRenamingType('session');
    setRenameValue(currentTitle || 'New chat');
  }, []);

  const startRenameFolder = useCallback((id: string, currentName: string) => {
    setRenamingId(id);
    setRenamingType('folder');
    setRenameValue(currentName);
  }, []);

  const startRenameGraph = useCallback((id: string, currentTitle: string) => {
    setRenamingId(id);
    setRenamingType('graph');
    setRenameValue(currentTitle || 'Untitled graph');
  }, []);

  const commitRename = useCallback(() => {
    if (renamingId && renameValue.trim()) {
      if (renamingType === 'session') {
        renameSession(renamingId, renameValue.trim());
      } else if (renamingType === 'folder') {
        renameFolder(renamingId, renameValue.trim());
      } else if (renamingType === 'graph') {
        renameGraph(renamingId, renameValue.trim());
      }
    }
    setRenamingId(null);
    setRenamingType(null);
    setRenameValue('');
  }, [renamingId, renamingType, renameValue, renameSession, renameFolder, renameGraph]);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenamingType(null);
    setRenameValue('');
  }, []);

  // ── Download helpers ─────────────────────────────────────────────────

  // Sidebar sessions are summary stubs; load the turns before rendering.
  const downloadChat = useCallback(async (sessionId: string, detailed: boolean) => {
    const stub = sessions.find((s) => s.id === sessionId);
    if (!stub) return;
    const [session] = await hydrateSessions(qc, [stub]);
    const md = detailed ? chatToMarkdownDetailed(session) : chatToMarkdownPlain(session);
    const filename = `${sanitizeFilename(session.title)}-${formatDate()}.md`;
    triggerDownload(md, filename);
  }, [sessions, qc]);

  const exportChatDebug = useCallback(async (sessionId: string) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (!session) return;
    // Fetch fresh rather than from the query cache: the point is to capture
    // exactly what the server persisted, including the latest turn.
    let detail: ChatDetail | null = null;
    let fetchError: string | undefined;
    if (session.serverChatId) {
      try {
        detail = await getChat(session.serverChatId);
      } catch (err) {
        fetchError = err instanceof Error ? err.message : String(err);
      }
    }
    const bundle = buildChatDebugBundle(session, detail, fetchError);
    const filename = `${sanitizeFilename(session.title)}-debug-${formatDate()}.json`;
    triggerDownload(JSON.stringify(bundle, null, 2), filename, 'application/json;charset=utf-8');
    if (fetchError) toast.warning(`Server record unavailable (${fetchError}); exported browser copy`);
  }, [sessions]);

  // ── Folder creation ──────────────────────────────────────────────────

  const handleCreateFolder = useCallback(() => {
    createFolder('New Folder');
  }, [createFolder]);

  // ── Switch session + navigate ────────────────────────────────────────

  const handleSwitchSession = useCallback((id: string) => {
    switchSession(id);
    navigate('/chat');
  }, [switchSession, navigate]);

  // ── Switch graph + navigate ──────────────────────────────────────────

  const handleSwitchGraph = useCallback((id: string) => {
    setActiveGraphId(id);
    navigate(`/graph?savedGraphId=${id}`);
  }, [setActiveGraphId, navigate]);

  // ── Clear all (route-aware) ──────────────────────────────────────────

  const handleClearAll = useCallback(() => {
    if (activeRoute === 'graph') {
      clearAllGraphs();
    } else {
      clearAllSessions();
    }
    setConfirmClearAll(false);
  }, [activeRoute, clearAllGraphs, clearAllSessions]);

  const clearAllLabel = activeRoute === 'graph' ? 'saved graphs' : 'chats';

  return (
    <aside className={`malpedia-sidebar${collapsed ? ' collapsed' : ''}`}>
      <button
        className="malpedia-sidebar-toggle"
        onClick={onToggleCollapse}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="3" y1="4" x2="15" y2="4" />
          <line x1="3" y1="9" x2="15" y2="9" />
          <line x1="3" y1="14" x2="15" y2="14" />
        </svg>
      </button>

      <div className="malpedia-sidebar-inner">
        {/* Navigation */}
        <nav className="malpedia-sidebar-nav">
          <NavLink
            to="/"
            end
            data-page="home"
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 8l6-5 6 5v6a1 1 0 0 1-1 1h-3v-4H6v4H3a1 1 0 0 1-1-1z" />
            </svg>
            Home
          </NavLink>
          <NavLink
            to="/chat"
            data-page="chat"
            onClick={() => {
              if (activeRoute !== 'chat') createSession();
            }}
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H5l-3 3z" />
            </svg>
            Chat
          </NavLink>
          <NavLink
            to="/graph"
            data-page="graph"
            onClick={() => {
              setActiveGraphId(null);
            }}
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="4" cy="4" r="2" />
              <circle cx="12" cy="4" r="2" />
              <circle cx="8" cy="12" r="2" />
              <line x1="5.5" y1="5.5" x2="7" y2="10.5" />
              <line x1="10.5" y1="5.5" x2="9" y2="10.5" />
            </svg>
            Graph
          </NavLink>
          <NavLink
            to="/overview"
            data-page="overview"
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="4" cy="4" r="2" />
              <circle cx="12" cy="4" r="2" />
              <circle cx="4" cy="12" r="2" />
              <circle cx="12" cy="12" r="2" />
              <line x1="6" y1="4" x2="10" y2="4" />
              <line x1="6" y1="12" x2="10" y2="12" />
              <line x1="4" y1="6" x2="4" y2="10" />
              <line x1="12" y1="6" x2="12" y2="10" />
            </svg>
            Atlas
          </NavLink>
          <NavLink
            to="/search"
            data-page="search"
            title="Search (press /)"
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="7" cy="7" r="4.5" />
              <line x1="10.5" y1="10.5" x2="14" y2="14" />
            </svg>
            Search
          </NavLink>
          <NavLink
            to="/explore"
            data-page="explore"
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="8" cy="8" r="6" />
              <polygon points="10.5,5.5 9,9 5.5,10.5 7,7" />
            </svg>
            Explore
          </NavLink>
          <NavLink
            to="/trends"
            data-page="trends"
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="2,12 5,8 8,10 13,3" />
              <polyline points="10,3 13,3 13,6" />
            </svg>
            Trends
          </NavLink>
        </nav>

        {/* Session / graph list: route-dependent */}
        <div className="malpedia-sidebar-sessions">
          {activeRoute === 'chat' && (
            <div className="malpedia-chatlist-tools">
              <label className="malpedia-chatlist-filter">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                  <circle cx="7" cy="7" r="4.5" />
                  <line x1="10.5" y1="10.5" x2="14" y2="14" />
                </svg>
                <input
                  type="search"
                  value={chatFilter}
                  onChange={(e) => setChatFilter(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape' && chatFilter) {
                      e.stopPropagation();
                      setChatFilter('');
                    }
                  }}
                  placeholder="Filter chats…"
                  aria-label="Filter chats by title or message text"
                />
              </label>
              {selectedIds.size > 0 && (
                <div className="malpedia-chatlist-bulk" role="toolbar" aria-label="Selected chats">
                  <b aria-live="polite">{selectedIds.size} selected</b>
                  <select
                    className="malpedia-chatlist-bulk-move"
                    value=""
                    onChange={(e) => handleBulkMove(e.target.value)}
                    aria-label="Move selected chats to folder"
                  >
                    <option value="" disabled>Move…</option>
                    {folders.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                    <option value="__none__">No folder</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => openExportDialog({ type: 'selection', ids: Array.from(selectedIds) })}
                  >
                    Export
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => setConfirmBulkDelete(true)}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="malpedia-chatlist-bulk-clear"
                    onClick={() => setSelectedIds(new Set())}
                    aria-label="Clear selection"
                    title="Clear selection (Esc)"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          )}
          {activeRoute === 'chat' && (
            <FolderTree
              sessions={sessions}
              runningIds={runningIds}
              folders={folders}
              activeSessionId={activeSessionId}
              folderCollapseState={folderCollapseState}
              renamingId={renamingType === 'session' || renamingType === 'folder' ? renamingId : null}
              renamingType={renamingType === 'session' || renamingType === 'folder' ? renamingType : null}
              renameValue={renameValue}
              filter={chatFilter}
              getSearchTexts={getSearchTexts}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onToggleFolderCollapse={toggleFolderCollapse}
              onSwitchSession={handleSwitchSession}
              onStartRenameSession={startRenameSession}
              onStartRenameFolder={startRenameFolder}
              onRenameValueChange={setRenameValue}
              onCommitRename={commitRename}
              onCancelRename={cancelRename}
              onChatMenuOpen={(sessionId, rect) => {
                setFolderMenuState(null);
                setGraphMenuState(null);
                setChatMenuState({ sessionId, rect });
              }}
              onFolderMenuOpen={(folderId, rect) => {
                setChatMenuState(null);
                setGraphMenuState(null);
                setFolderMenuState({ folderId, rect });
              }}
              onChatDownload={(sessionId) => void downloadChat(sessionId, false)}
              onCreateFolder={handleCreateFolder}
              onMoveSessionsToFolder={moveSessionsToFolder}
            />
          )}
          {activeRoute === 'graph' && (
            <SavedGraphList
              graphs={savedGraphs}
              activeGraphId={activeGraphId}
              renamingId={renamingType === 'graph' ? renamingId : null}
              renameValue={renameValue}
              onSwitchGraph={handleSwitchGraph}
              onStartRename={startRenameGraph}
              onRenameValueChange={setRenameValue}
              onCommitRename={commitRename}
              onCancelRename={cancelRename}
              onGraphMenuOpen={(graphId, rect) => {
                setChatMenuState(null);
                setFolderMenuState(null);
                setGraphMenuState({ graphId, rect });
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="malpedia-sidebar-footer">
          {(activeRoute === 'chat' || activeRoute === 'graph') && (
            <button className="malpedia-sidebar-clear-all" onClick={() => setConfirmClearAll(true)}>
              Clear All
            </button>
          )}
          <img
            src={resolved === 'dark' ? fkieLogoDark : fkieLogo}
            alt="Fraunhofer FKIE"
            className="malpedia-sidebar-fkie-logo"
          />
        </div>
      </div>

      {/* Chat context menu */}
      {chatMenuState && (() => {
        const session = sessions.find((s) => s.id === chatMenuState.sessionId);
        if (!session) return null;
        return (
          <ChatItemMenu
            sessionId={chatMenuState.sessionId}
            sessionFolderId={session.folderId}
            anchorRect={chatMenuState.rect}
            folders={folders}
            pinned={!!session.pinned}
            onTogglePin={session.temporary ? undefined : () => setSessionPinned(session.id, !session.pinned)}
            onRename={() => startRenameSession(chatMenuState.sessionId, session.title)}
            onMoveToFolder={(folderId) => moveSessionToFolder(chatMenuState.sessionId, folderId)}
            onDownloadPlain={() => void downloadChat(chatMenuState.sessionId, false)}
            onDownloadDetailed={() => void downloadChat(chatMenuState.sessionId, true)}
            onExportDebug={() => void exportChatDebug(chatMenuState.sessionId)}
            onDelete={() => deleteSession(chatMenuState.sessionId)}
            onClose={() => setChatMenuState(null)}
          />
        );
      })()}

      {/* Folder context menu */}
      {folderMenuState && (() => {
        const folder = folders.find((f) => f.id === folderMenuState.folderId);
        if (!folder) return null;
        const chatCount = sessions.filter((s) => s.folderId === folder.id).length;
        return (
          <FolderMenu
            folderId={folderMenuState.folderId}
            folderName={folder.name}
            chatCount={chatCount}
            anchorRect={folderMenuState.rect}
            onRename={() => startRenameFolder(folderMenuState.folderId, folder.name)}
            onExportFolder={() => openExportDialog({ type: 'folder', folderId: folderMenuState.folderId })}
            onDelete={() => deleteFolder(folderMenuState.folderId)}
            onClose={() => setFolderMenuState(null)}
          />
        );
      })()}

      {/* Graph context menu */}
      {graphMenuState && (() => {
        const g = savedGraphs.find((sg) => sg.id === graphMenuState.graphId);
        if (!g) return null;
        return (
          <GraphItemMenu
            anchorRect={graphMenuState.rect}
            onRename={() => startRenameGraph(graphMenuState.graphId, g.title)}
            onDelete={() => deleteGraph(graphMenuState.graphId)}
            onClose={() => setGraphMenuState(null)}
          />
        );
      })()}

      {/* Bulk delete confirmation */}
      {confirmBulkDelete && selectedIds.size > 0 && (
        <ClearAllDialog
          onConfirm={handleBulkDelete}
          onClose={() => setConfirmBulkDelete(false)}
          message={`Delete ${selectedIds.size} selected chat${selectedIds.size === 1 ? '' : 's'}? This action cannot be undone.`}
          confirmLabel="Delete"
        />
      )}

      {/* Clear all confirmation dialog */}
      {confirmClearAll && (
        <ClearAllDialog
          onConfirm={handleClearAll}
          onClose={() => setConfirmClearAll(false)}
          label={clearAllLabel}
        />
      )}
    </aside>
  );
}
